import React from 'react'
import { makeStyles, FormControl } from '@material-ui/core'
import type { Theme } from '@material-ui/core'
import classnames from 'classnames'
import { default as MuiInputAdornment } from '@material-ui/core/InputAdornment'
import type { SvgIconProps } from '@material-ui/core/SvgIcon'
import Input from '../Input'
import type { InputProps } from '../Input'
import type { StyleClasses } from '../../styles'
import FormattedInput from '../FormattedInput'

const useStyles = makeStyles<Theme, InputFieldProps>((theme) => ({
  root: {
    '& label > span': {
      color: theme.palette.error.main,
      position: 'absolute',
      top: 2.5
    },
    '& textarea': {
      minHeight: '38px'
    }
  },
  inputRoot: {},
  inputInput: {},
  adornment: {
    marginLeft: theme.typography.pxToRem(7),
    marginRight: theme.typography.pxToRem(7),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.typography.pxToRem(12),
      marginRight: theme.typography.pxToRem(12)
    }
  },
  adornmentNoRightMargin: {
    marginRight: 0,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0
    }
  },
  inputWrapper: {},
  inputFocused: {
    '& $adornment': {
      color: theme.palette.primary.main
    }
  },
  clickableIcon: {
    cursor: 'pointer'
  },
  input: {},
  icon: {},
  label: {},
  hasLabel: {},
  hasSublabel: {},
  focused: {}
}))

export type InputFieldFormat =
  | 'price'
  | 'quantity'
  | 'hours'
  | 'dimension'
  | 'rate'
  | 'taxRate'
  | 'coordinate'
  | 'custom'

export interface InputFieldProps extends InputProps {
  classes?: InputProps['classes'] & StyleClasses<typeof useStyles>
  autoSelect?: boolean
  icon?: React.ComponentType<SvgIconProps>

  toolTip?: string | React.ReactNode
  format?: InputFieldFormat
  suffix?: string
  prefix?: string
  pattern?: { expression: string; title: string }
  onIconClick?: (ev: React.MouseEvent) => void
  noAdornmentMargin?: boolean
  allowZero?: boolean
  allowNegativeNumberInputs?: boolean
}

function InputField(props: InputFieldProps) {
  const classes = useStyles(props)

  const {
    icon: Icon,
    label: labelText,
    sublabel: sublabelText,
    inlineLabel,

    required,
    pattern,

    startAdornment,
    endAdornment,
    inputRef,

    className,
    margin,
    autoSelect: _autoSelect,
    style,
    error,
    classes: _classesProps,
    noAdornmentMargin,
    allowNegativeNumberInputs: _allowNegativeNumberInputs,
    ...baseInputProps
  } = props

  return (
    <FormControl
      className={className}
      classes={{ root: classes.root }}
      fullWidth={baseInputProps.fullWidth}
      required={required}
      margin={margin}
      error={error}
      style={style}
    >
      <Input
        ref={inputRef}
        className={className}
        label={labelText}
        inputProps={pattern ? { pattern: pattern.expression, title: pattern.title } : {}}
        inlineLabel={inlineLabel}
        sublabel={sublabelText}
        toolTip={props.toolTip}
        error={error}
        startAdornment={
          (Icon || startAdornment) && (
            <MuiInputAdornment position="start" classes={{ root: classes.adornment }}>
              {Icon && (
                <Icon
                  className={classnames(classes.icon, { [classes.clickableIcon]: props.onIconClick })}
                  onClick={props.onIconClick}
                />
              )}
              {startAdornment}
            </MuiInputAdornment>
          )
        }
        endAdornment={
          endAdornment && (
            <MuiInputAdornment
              position="end"
              classes={{
                root: classnames(classes.adornment, { [classes.adornmentNoRightMargin]: noAdornmentMargin })
              }}
            >
              {endAdornment}
            </MuiInputAdornment>
          )
        }
        classes={{
          focused: classes.inputFocused
        }}
        required={required}
        {...getFormatProps(props)}
        {...baseInputProps}
      />
    </FormControl>
  )
}

const getFormatProps = (props) => {
  const { format, name, prefix, suffix, allowZero, allowNegativeNumberInputs } = props
  if (format) {
    return {
      inputComponent: FormattedInput,
      inputProps: { format, name, prefix, suffix, allowZero, allowNegativeNumberInputs }
    } as any
  }
  return {}
}

export default InputField
